import { Avatar } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableLoader from "@/Components/common/TableLoader";
import TableNoContent from "@/Components/common/TableNoContent";
import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchBookedCandidates,
  setBookedCandidatesLoading,
  updateListView,
  updateSelectedCandidate,
  updateSelectedContract,
  updatePagination,
  updateSorting,
  updateToggleSelected,
  fetchBookedShifts,
  toggleSelectedAll,
  deSelectAllCandidate,
  updateSelectedContractRefNo,
} from "@/reducers/client_candidates/candidatesbooked";
import { fetchCandidateTypes } from "@/reducers/clientdashboard";
import {
  expandButtonColumn,
  getActionByHtml,
  getColumnNormalHtml,
  totalDaysColumn,
} from "@/utilities/datatable/columnFunctions";
import {
  sortByDate,
  sortByNumber,
  sortByText,
} from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { getComplianceClass } from "@/utilities/utils";

import { Actions } from "./Booked/Actions";
import { FilterModal } from "./Booked/FilterModal";
import { SearchFilter } from "./Booked/SearchFilter";
import { ContractsShifts } from "./common/ContractsShifts";
import { Miles } from "./common/Miles";
import { SelectedCountText } from "./common/SelectedCountText";
import SwitchListView from "./common/SwitchListView";
import CoverIcon from "../../../../Icons/CoverIcon";
import DataTablePaginationLoader from "../../../../Skeletons/DataTablePaginationLoader";
import CandidateStatusAndInfo from "../../dataTables/CandidateStatusAndInfo";
import ClientPayPopover from "../../dataTables/ClientPayPopover";
import BlurredText from "../../managedService/BlurredText";

/**
 * Function for rendering a list of booked candidates for shifts.
 *
 * @return {JSX.Element} The JSX for the booked candidates list component
 */
const BookedList = () => {
  const dispatch = useDispatch();

  const {
    bookedCandidates,
    selectedCandidate,
    isLoading,
    pagination,
    filters,
    toggleSelected,
    listView,
    totalCount,
    isSelectedAll,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientCandidatesBooked);
  const { clientUserAllPermissions } = useSelector((state) => state.session);

  const [data, setData] = useState([]);
  useEffect(() => {
    if (selectedCandidate.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedCandidate]);

  useEffect(() => {
    setData(bookedCandidates);
  }, [bookedCandidates]);
  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_name"),
      selector: (row) => (
        <div className="flex gap-[14px]" data-tag="allowRowEvents">
          <div className="min-w-[49px]">
            <Avatar
              alt={row.candidate_name
                .trim()
                .split(" ")[1]
                .charAt(0)
                .toUpperCase()}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_view_candidate_profile ===
              1 ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                !clientUserAllPermissions ||
                clientUserAllPermissions.is_allow_to_view_contract_detail ===
                  1 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={routingConstants.contractDetail(
                        row.route_contract_id,
                      )}
                      className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor"
                    >
                      {row.contract_ref_no}
                    </Link>
                    {row?.is_substitute_contract && listView !== "shifts" ? (
                      <div
                        div
                        className={`flex h-[25px] w-[37px] cursor-pointer items-center gap-2 rounded-[4px] bg-lightOrange px-2 py-1 text-center font-medium leading-[23px] text-darkOrange`}
                      >
                        <CoverIcon cls="text-darkOrange" />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <span className="line-clamp-1 cursor-default text-[12px] font-medium leading-[normal] text-txtColor">
                      {row.contract_ref_no}
                    </span>
                    {row?.is_substitute_contract && listView !== "shifts" ? (
                      <div
                        div
                        className={`flex h-[25px] w-[37px] cursor-pointer items-center gap-2 rounded-[4px] bg-lightOrange px-2 py-1 text-center font-medium leading-[23px] text-darkOrange`}
                      >
                        <CoverIcon cls="text-darkOrange" />
                      </div>
                    ) : null}
                  </div>
                )
              }
            />
          </div>
        </div>
      ),
      width: "19%",
    },
    {
      name: "Contact",
      sortable: true,
      sortField: "candidate_email",

      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_email"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          <BlurredText type="phone">
            {getColumnNormalHtml(row.mobile_phone_number, "truncate")}
          </BlurredText>
          <BlurredText type="email">
            <span className="truncate text-[12px] font-medium leading-[normal] text-txtColor">
              {row.candidate_email}
            </span>
          </BlurredText>
        </div>
      ),
      width: "13%",
    },
    {
      name: "Miles",
      // sortable: true,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "miles"),
      selector: (row) => <Miles miles={row.miles} />,
      width: "95px",
    },
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "role_name"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <span className="truncate text-[12px] font-medium leading-[normal] text-txtColor">
            {row.license_number}
          </span>
        </div>
      ),
      width: listView === "shifts" ? "6rem" : "130px",
    },
    {
      name: "Booked by",
      // sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "booked_by_name"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(
            row.booked_by_name,
            row.booked_at,
            row.booked_from,
            "",
          )}
        </div>
      ),
      width: listView === "shifts" ? "11.5%" : "14%",
    },
    {
      name: "Shift Date",
      sortable: true,
      sortField:
        listView === "shifts" ? "contract_date" : "contract_start_date",
      sortFunction: (rowA, rowB) => {
        if (listView === "shifts") {
          // Sort by shift_date if listView is "shifts"
          return sortByDate(
            moment(rowA.shift_date).format("DD/MM/YYYY"),
            moment(rowB.shift_date).format("DD/MM/YYYY"),
          );
        } else {
          // Sort by contract_start_date if listView is not "shifts"
          return sortByDate(
            moment(rowA.contract_start_date).format("DD/MM/YYYY"),
            moment(rowB.contract_start_date).format("DD/MM/YYYY"),
          );
        }
      },
      selector: (row) => (
        <div
          className="flex flex-col items-end gap-1"
          data-tag="allowRowEvents"
        >
          {listView === "shifts" ? (
            <>
              {getColumnNormalHtml(moment(row.shift_date).format("DD/MM/YYYY"))}
              <span
                className={`h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
                  row.profile_percent,
                )}`}
              >
                {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
              </span>
            </>
          ) : (
            <>
              {getColumnNormalHtml(
                moment(row.contract_start_date).format("DD/MM/YYYY"),
              )}
              <span className="text-[14px] font-semibold leading-[3px] text-black">
                -
              </span>
              {getColumnNormalHtml(
                moment(row.contract_end_date).format("DD/MM/YYYY"),
              )}
            </>
          )}
        </div>
      ),
      width: listView === "shifts" ? "7.3%" : "8%",
    },
    ...(listView === "shifts"
      ? [
          {
            name: "Shift",
            sortable: true,
            sortField: "contract_start_time",

            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.contract_start_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.contract_end_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
                )}
                <span className="text-[14px] font-semibold leading-[0px] text-black">
                  -
                </span>
                {getColumnNormalHtml(
                  moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
                )}
              </div>
            ),
            width: "60px",
          },
          {
            name: "Break/Admin",
            sortable: true,
            sortField: "un_paid_break_hour",

            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.break_time, "HH:mm:ss").format("HH:mm"),
                )}
                <span className="text-[14px] font-semibold leading-[0px] text-black">
                  -
                </span>
                {getColumnNormalHtml(
                  moment(row.admin_time, "HH:mm:ss").format("HH:mm"),
                )}
              </div>
            ),
            width: "8%",
          },
          {
            name: "Visit",
            sortable: true,
            sortField: "contract_no_of_visit",

            sortFunction: (rowA, rowB) =>
              sortByNumber(rowA, rowB, "no_of_visit"),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                <span className="text-[14px] font-semibold leading-[normal] text-black">
                  {row.no_of_visit ? row.no_of_visit : 0}
                </span>
              </div>
            ),
            width: "4%",
          },
          {
            name: "Unit",
            selector: (row) => (
              <div className="flex flex-col gap-1" data-tag="allowRowEvents">
                <span className="text-[14px] font-semibold leading-[normal] text-black">
                  {row.contract_duration !== "-"
                    ? getColumnNormalHtml(
                        Number(`${row.contract_duration}`).toFixed(2),
                      )
                    : "0.00"}
                </span>
              </div>
            ),
            width: "4.5%",
          },
          {
            name: "Total",
            wrap: true,
            selector: (row) => (
              <ClientPayPopover
                pay_arr={row?.client_pay_arr}
                shift_rate={
                  Number(row.rate) +
                  Number(row.cover_request_awarded_reward || 0) -
                  Number(row.cover_request_awarded_penalty || 0)
                }
                is_substitute_contract={row?.is_substitute_contract}
                cover_request_awarded_penalty={
                  row?.cover_request_awarded_penalty
                }
                cover_request_awarded_reward={row.cover_request_awarded_reward}
                content={(content) => (
                  <div
                    className="flex flex-col gap-1 break-keep"
                    data-tag="allowRowEvents"
                  >
                    {getColumnNormalHtml(content, "cursor-pointer")}
                  </div>
                )}
              />
            ),
            width: "auto",
          },
        ]
      : [
          {
            name: "Total Days",
            selector: (row) => (
              <div className="flex flex-col items-center">
                <p className="text-[14px] font-semibold leading-[normal] text-black">
                  {row.days_total ? row.days_total : "0"}
                </p>
                <span
                  className={`mt-[2px] h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
                    row.profile_percent,
                  )}`}
                >
                  {row.profile_percent == "-"
                    ? "0%"
                    : row.profile_percent + "%"}
                </span>
              </div>
            ),
            width: "8.5%",
          },
          totalDaysColumn("Booked", "days_booked", "70px", false),
          totalDaysColumn(
            "Worked",
            "days_worked",
            "6.5%",
            false,
            routingConstants.workedCandidate,
          ),
          expandButtonColumn(),
        ]),
  ];
  const dataTableRef = useRef();
  const Expandedrow = ({ data }) => (
    <ContractsShifts
      shifts={data.shifts}
      dateFormat="YYYY-MM-DD"
      substitueCon={data?.is_substitute_contract}
    />
  );
  /**
   * Handles the scroll event on a table and fetches booked candidates if scrolled to the bottom.
   */

  // Comman function for fetching data in list lazy loading
  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchBookedCandidates({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const firstViewRender = useRef(true);
  useEffect(() => {
    if (firstViewRender.current) {
      firstViewRender.current = false;
      return;
    }
    // Fetching list on changing view
    dispatch(setBookedCandidatesLoading(true));
    dispatch(
      updateSorting({
        sortByColumn: "",
        sortByOrder: "",
      }),
    );
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(fetchBookedCandidates(filters));
    dispatch(
      fetchCandidateTypes({
        bookedView: listView,
      }),
    );
  }, [listView]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(setBookedCandidatesLoading(true));
    dispatch(deSelectAllCandidate());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(fetchBookedCandidates());
  };

  return (
    <div className="InvitedList_Wrapper mb-[58px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="main-heading tour-booked-heading font-semibold leading-[normal] text-txtDark">
              Candidates Confirmed for Shifts
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedCandidate.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedCandidate(val));
              }}
              currentDataLength={data.length}
            />
          </div>
          <div className="flex gap-[20px]">
            <SwitchListView
              value={listView}
              handleClick={(value) => {
                dispatch(deSelectAllCandidate());
                dispatch(updateListView(value));
              }}
            />
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={data}
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            noDataComponent={<TableNoContent />}
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              // contract_ref_no
              let selectedIDS = state.selectedRows.map((item) => {
                return item.candidate_id;
              });
              let selectedContracts = state.selectedRows.map((item) => {
                return item.contract_id;
              });
              let contractReferenceNo = state.selectedRows.map((item) => {
                return item.contract_ref_no;
              });
              dispatch(updateSelectedContract(selectedContracts));
              dispatch(updateSelectedCandidate(selectedIDS));
              dispatch(updateSelectedContractRefNo(contractReferenceNo));
            }}
            className="_DataTable Booked_DataTable"
            clearSelectedRows={toggleSelected}
            expandableRows={listView !== "shifts"}
            expandOnRowClicked
            expandableRowsComponent={Expandedrow}
            onRowExpandToggled={(state, row) => {
              if (state) {
                dispatch(
                  fetchBookedShifts({
                    contractId: row.contract_id,
                    //     candidate_id: row.candidate_id,
                  }),
                );
              }
            }}
          />
          {!isLoading && isPaginationLoading ? (
            <DataTablePaginationLoader />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BookedList;
